<ng-container *transloco="let t; prefix: 'anc.shared.header'">
  <div class="announce">
    <div class="announce-in">
      <span class="announce-tag">{{ t('announceTag') }}</span>
      <span>{{ t('announceText') }}</span>
      <a [routerLink]="href({ path: '/vps-performance' })" [queryParams]="{ type: 'lxc' }">{{ t('announceCta') }}</a>
    </div>
  </div>

  <div class="util">
    <div class="util-in">
      <div class="util-group">
        <a
          class="util-status"
          [class.is-degraded]="serviceState() === 'degraded'"
          [routerLink]="href({ path: '/status' })"
        >
          <span class="pulse" aria-hidden="true"></span><span class="util-long">{{ t('statusLong') }}</span
          ><span class="util-short">{{ t('statusShort') }}</span>
          @if (serviceState(); as state) {
            <b>{{ state === 'operational' ? t('statusOperational') : t('statusDegraded') }}</b>
          }
        </a>
        <span class="util-sep" aria-hidden="true"></span>
        <a class="util-link util-phone" href="tel:+33184754308">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M5 4h3.5l1.6 4-2.2 1.4a11 11 0 0 0 6.7 6.7l1.4-2.2 4 1.6V19a1.5 1.5 0 0 1-1.6 1.5A16 16 0 0 1 3.5 5.6 1.5 1.5 0 0 1 5 4Z"
            />
          </svg>
          {{ t('phone') }}
        </a>
        <span class="util-sep util-mail" aria-hidden="true"></span>
        <a class="util-link util-mail" href="mailto:contact&#64;ancelade.com">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 6h16v12H4z" />
            <path d="m4 7 8 6 8-6" />
          </svg>
          contact&#64;ancelade.com
        </a>
      </div>
      <div class="util-group">
        <a class="util-link" [routerLink]="href({ path: '/contact' })" (click)="closeAll()">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M9.6 9.4a2.5 2.5 0 1 1 3.4 2.3c-.6.3-1 .8-1 1.5v.6M12 17h.01" />
          </svg>
          {{ t('support') }}
        </a>
        <span class="util-sep" aria-hidden="true"></span>
        <div class="lang">
          <button
            type="button"
            class="lang-trg"
            [attr.aria-label]="t('langTrigger')"
            aria-controls="anc-lang-menu"
            [attr.aria-expanded]="langOpen()"
            (click)="langOpen.set(!langOpen())"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M3 12h18M12 3c2.6 3 2.6 15 0 18M12 3c-2.6 3-2.6 15 0 18" />
            </svg>
            {{ currentLang() | uppercase }}
            <svg class="caret" viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
          </button>
          <ul class="lang-menu" id="anc-lang-menu" [hidden]="!langOpen()" [attr.aria-label]="t('langMenu')">
            @for (option of languages; track option.code) {
              <li>
                <a
                  [routerLink]="languagePath(option.code)"
                  queryParamsHandling="preserve"
                  [preserveFragment]="true"
                  [attr.hreflang]="option.code"
                  [attr.lang]="option.code"
                  [class.is-on]="option.code === currentLang()"
                  [attr.aria-current]="option.code === currentLang() ? 'true' : null"
                  (click)="langOpen.set(false)"
                >
                  <span class="anc-mono">{{ option.code | uppercase }}</span
                  >{{ option.label }}
                </a>
              </li>
            }
          </ul>
        </div>
      </div>
    </div>
  </div>

  <header class="hdr">
    <div class="hdr-in">
      <a class="brand" [routerLink]="home()" [attr.aria-label]="t('homeLabel')">
        <img src="/brand/ancelade-logo.svg" alt="Ancelade" width="117" height="30" />
      </a>

      <nav class="navi" [attr.aria-label]="t('navLabel')">
        @for (universe of universes(); track universe.key) {
          <div
            class="u-wrap"
            [class]="'u-' + universe.key"
            [class.is-open]="openKey() === universe.key"
            [class.is-locked]="locked()"
            (mouseleave)="locked.set(false)"
          >
            <button
              type="button"
              class="u-trg"
              [attr.aria-expanded]="openKey() === universe.key"
              [attr.aria-controls]="'menu-' + universe.key"
              (click)="toggle(universe)"
            >
              <i aria-hidden="true"></i>{{ universe.name }}
              <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
            </button>

            <div class="u-panel" [id]="'menu-' + universe.key">
              <div class="u-intro">
                <span class="u-kicker">{{ t('universeKicker') }}</span>
                <span class="u-name">{{ universe.name }}</span>
                <b class="u-tagline">{{ universe.tagline }}</b>
                <span class="u-desc">{{ universe.description }}</span>
                <a
                  class="u-all"
                  [routerLink]="href(universe.link)"
                  [queryParams]="universe.link.queryParams"
                  (click)="closeAll()"
                  >{{ t('universeAll', { name: universe.name }) }}</a
                >
              </div>

              <div class="u-items">
                @for (item of universe.items; track item.id) {
                  <a
                    class="u-item"
                    [routerLink]="href(item.link)"
                    [queryParams]="item.link.queryParams"
                    (click)="closeAll()"
                  >
                    <span class="u-ico" aria-hidden="true">
                      <img [ngSrc]="'img/mega-menu/' + item.icon + '.webp'" width="60" height="60" [alt]="item.title" />
                    </span>
                    <span class="u-item-text">
                      <b>{{ item.title }}</b>
                      <span>{{ item.description }}</span>
                    </span>
                  </a>
                }
              </div>

              <div class="u-side">
                <a
                  class="u-hl"
                  [routerLink]="href(universe.highlight.link)"
                  [queryParams]="universe.highlight.link.queryParams"
                  (click)="closeAll()"
                >
                  <span class="u-hl-label">{{ universe.highlight.label }}</span>
                  <span class="u-hl-metric">{{ universe.highlight.metric }}</span>
                  <span class="u-hl-caption">{{ universe.highlight.caption }}</span>
                </a>
                <p class="u-note">
                  {{ universe.note.text }}<b>{{ universe.note.detail }}</b>
                </p>
              </div>

              <a
                class="u-banner"
                [routerLink]="href(universe.banner.link)"
                [queryParams]="universe.banner.link.queryParams"
                (click)="closeAll()"
              >
                <span class="u-banner-text">
                  <b>{{ universe.banner.title }}</b>
                  <span>{{ universe.banner.caption }}</span>
                </span>
                <span class="u-banner-tags">
                  @for (tag of universe.banner.tags; track tag) {
                    <span class="tag">{{ tag }}</span>
                  }
                  <span class="go" aria-hidden="true">→</span>
                </span>
              </a>
            </div>
          </div>
        }
        <a class="navi-link" [routerLink]="href({ path: '/about' })" (click)="closeAll()">{{ t('about') }}</a>
        <a class="navi-link" [routerLink]="href({ path: '/contact' })" (click)="closeAll()">{{ t('contact') }}</a>
      </nav>

      <a class="client" [href]="panelUrl">{{ t('clientArea') }}</a>
      <a class="btn btn-primary cta" [routerLink]="home()" fragment="vps">{{ t('cta') }}</a>
      <button
        type="button"
        class="burger"
        [attr.aria-expanded]="mobileOpen()"
        aria-controls="home3-mobile-menu"
        [attr.aria-label]="mobileOpen() ? t('menuClose') : t('menuOpen')"
        (click)="toggleMobile()"
      >
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          @if (mobileOpen()) {
            <path d="M6 6l12 12M18 6 6 18" />
          } @else {
            <path d="M4 7h16M4 12h16M4 17h10" />
          }
        </svg>
      </button>
    </div>

    @if (mobileOpen()) {
      <div class="mobile" id="home3-mobile-menu">
        @for (universe of universes(); track universe.key) {
          <section class="m-uni" [class]="'u-' + universe.key" [attr.aria-labelledby]="'m-' + universe.key">
            <h2 class="m-name" [id]="'m-' + universe.key"><i aria-hidden="true"></i>{{ universe.name }}</h2>
            <ul>
              @for (item of universe.items; track item.id) {
                <li>
                  <a [routerLink]="href(item.link)" [queryParams]="item.link.queryParams" (click)="closeAll()">
                    <b>{{ item.title }}</b
                    ><span>{{ item.description }}</span>
                  </a>
                </li>
              }
            </ul>
          </section>
        }
        <div class="m-foot">
          <a [routerLink]="href({ path: '/about' })" (click)="closeAll()">{{ t('about') }}</a>
          <a [routerLink]="href({ path: '/contact' })" (click)="closeAll()">{{ t('contact') }}</a>
          <a [href]="panelUrl">{{ t('clientArea') }}</a>
        </div>
      </div>
    }
  </header>
</ng-container>
